import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { debugLogger } from '@/utils/debugLogger';
import type { UserGems, GemTransaction } from '@/types/gems';

export const useGems = () => {
  const { user } = useAuth();
  const [balance, setBalance] = useState<number>(0);
  const [totalEarned, setTotalEarned] = useState<number>(0);
  const [totalSpent, setTotalSpent] = useState<number>(0);
  const [transactions, setTransactions] = useState<GemTransaction[]>([]);
  const [loading, setLoading] = useState(true);

  const refreshGems = useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoading(true);

      // Buscar saldo de gemas
      const { data, error } = await supabase
        .from('user_gems')
        .select('balance, total_earned, total_spent')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        debugLogger.error('Gems', 'Error fetching gems:', error);
        return;
      }

      if (data) {
        setBalance(data.balance);
        setTotalEarned(data.total_earned);
        setTotalSpent(data.total_spent);
      } else {
        // Usar upsert atômico para evitar race condition (múltiplas chamadas simultâneas)
        const { error: upsertError } = await supabase
          .from('user_gems')
          .upsert(
            { user_id: user.id, balance: 0, total_earned: 0, total_spent: 0 },
            { onConflict: 'user_id', ignoreDuplicates: true }
          );

        // Ignorar erro 23505 (duplicate key) silenciosamente - registro já existe
        if (upsertError && upsertError.code !== '23505') {
          debugLogger.error('Gems', 'Error creating gems record:', upsertError);
        }
        
        // Buscar dados após upsert
        const { data: freshData } = await supabase
          .from('user_gems')
          .select('balance, total_earned, total_spent')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (freshData) {
          setBalance(freshData.balance);
          setTotalEarned(freshData.total_earned);
          setTotalSpent(freshData.total_spent);
        }
      }

      debugLogger.debug('Gems', 'Gems data refreshed:', { balance: data?.balance });
    } catch (error) {
      debugLogger.error('Gems', 'Error refreshing gems:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  const fetchTransactions = useCallback(async (limit: number = 20) => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from('gem_transactions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        debugLogger.error('Gems', 'Error fetching transactions:', error);
        return;
      }

      setTransactions(data as GemTransaction[]);
    } catch (error) {
      debugLogger.error('Gems', 'Error fetching transactions:', error);
    }
  }, [user?.id]);

  useEffect(() => {
    refreshGems();
  }, [refreshGems]);

  return {
    balance,
    totalEarned,
    totalSpent,
    transactions,
    loading,
    refreshGems,
    fetchTransactions
  };
};
